import { inject } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';
import { FiltersValues } from '../../../models';
import { parseFiltersData } from '../../../utils';
import { FiltersStorage, NearbyGamesService } from '@find-games/infra';
import { CommonGameDto, NearbyGamesByLocationDto, NearbyGamesByLocationDtoLocation } from '@core-http';
import { GetMapMarkersQueryResult, MapMarkerLocationModel } from './get-map-markers.query-result';
import { GameDetailsModel, mapCommonGameDto, omit } from '@core';

export class GetMapMarkersQueryHandler {
  private readonly nearbyGamesService: NearbyGamesService = inject(NearbyGamesService);
  private readonly filtersStorage: FiltersStorage = inject(FiltersStorage);

  getMapMarkers(): Observable<GetMapMarkersQueryResult> {
    return this.filtersStorage.filters$.pipe(
      map((filters: FiltersValues) => {
        const adjustedFilters: Omit<
          FiltersValues,
          'maxDistanceMeters' | 'longitude' | 'latitude' | 'showPrivateBlurred'
        > = omit(filters, ['maxDistanceMeters', 'longitude', 'latitude', 'showPrivateBlurred']);

        return adjustedFilters as FiltersValues;
      }),
      map((filters: FiltersValues) => parseFiltersData(filters)),
      switchMap((filters: string) => this.nearbyGamesService.getMarkers(filters)),
      map((dto: NearbyGamesByLocationDto) => this.mapToQueryResult(dto))
    );
  }

  private mapToQueryResult(dto: NearbyGamesByLocationDto): GetMapMarkersQueryResult {
    const locations: MapMarkerLocationModel[] = dto.locations.map((location: NearbyGamesByLocationDtoLocation) =>
      this.mapLocationToModel(location)
    );

    return {
      locations
    };
  }

  private mapLocationToModel(location: NearbyGamesByLocationDtoLocation): MapMarkerLocationModel {
    const games: GameDetailsModel[] = location.games.map((game: CommonGameDto) => mapCommonGameDto(game));

    return {
      locationId: location.locationId,
      coordinates: {
        latitude: location.coordinates.latitude,
        longitude: location.coordinates.longitude
      },
      name: location.name,
      address: location.address,
      city: location.city,
      hasRelationWithSomeGame: location.hasRelationWithSomeGame,
      games
    };
  }
}
